import React from "react";
import { Box, Typography, Avatar, keyframes } from "@mui/material";
import { useSelector, useDispatch } from "react-redux";
import { useLocation } from "react-router-dom";
import { useAssistantChat } from "./AssistantChatContext";
import { openLoginPopup } from "../../redux/slices/authSlice";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";

const pulseAnimation = keyframes`
  0% { box-shadow: 0 4px 12px rgba(255, 140, 56, 0.2), 0 0 0 0 rgba(255, 140, 56, 0.6); }
  70% { box-shadow: 0 4px 12px rgba(255, 140, 56, 0.2), 0 0 0 12px rgba(255, 140, 56, 0); }
  100% { box-shadow: 0 4px 12px rgba(255, 140, 56, 0.2), 0 0 0 0 rgba(255, 140, 56, 0); }
`;

const twinkle = keyframes`
  0%, 100% { transform: scale(0) rotate(0deg); opacity: 0; }
  50% { transform: scale(1) rotate(180deg); opacity: 1; }
`;

const GlobalAssistantButton = () => {
  const dispatch = useDispatch();
  const { isAuthenticated } = useSelector((state) => state.auth);
  const { openChat, isOpen } = useAssistantChat();
  const location = useLocation();
  const [isCheckoutOpen, setIsCheckoutOpen] = React.useState(false);

  React.useEffect(() => {
    const handler = (e) => setIsCheckoutOpen(Boolean(e.detail?.isOpen));
    window.addEventListener("handpik:checkout_toggle", handler);
    return () => window.removeEventListener("handpik:checkout_toggle", handler);
  }, []);

  if (isCheckoutOpen) return null;

  const hiddenRoutes = [
    "/profile",
    "/dashboard",
    "/login",
    "/signup",
    "/admin-login",
    "/verify-email",
    "/forgot-password",
    "/brand-login",
    "/dashboard-brands",
  ];
  if (hiddenRoutes.some(route => location.pathname.startsWith(route))) return null;

  const isProductPage = location.pathname.startsWith("/product");

  const handleClick = () => {
    if (!isAuthenticated) {
      dispatch(openLoginPopup());
      return;
    }
    openChat(null);
  };

  return (
    <Box
      onClick={handleClick}
      sx={{
        position: "fixed",
        bottom: { xs: isProductPage ? 116 : 16, sm: isProductPage ? 116 : 24, md: 32 },
        right: { xs: 16, sm: 24, md: 32 },
        zIndex: 1000,
        display: "flex",
        alignItems: "center",
        gap: 1,
        px: { xs: 2, sm: 2.5 },
        py: { xs: 1, sm: 1.25 },
        borderRadius: "50px",
        background: "#FFFFFF",
        border: "2.5px solid #FF8C38",
        boxShadow: "0 4px 12px rgba(255, 140, 56, 0.2)",
        color: "#1a1a1a",
        transformOrigin: "bottom right",
        transform: isOpen ? "scale(0) translateY(20px)" : "scale(1) translateY(0)",
        opacity: isOpen ? 0 : 1,
        pointerEvents: isOpen ? "none" : "auto",
        transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
        animation: isOpen ? "none" : `${pulseAnimation} 2s infinite`,
        "&:hover": {
          background: "#FFF5EE",
          boxShadow: "0 6px 16px rgba(255, 140, 56, 0.3)",
        },
        "&:active": {
          transform: isOpen ? "scale(0) translateY(20px)" : "scale(0.95)",
        },
        cursor: "pointer",
      }}
    >
      <AutoAwesomeIcon
        sx={{
          position: "absolute",
          top: -10,
          right: -10,
          color: "#FF6B00",
          fontSize: "26px",
          animation: `${twinkle} 2s ease-in-out infinite`,
          zIndex: 2,
        }}
      />
      <AutoAwesomeIcon
        sx={{
          position: "absolute",
          bottom: -8,
          left: -8,
          color: "#FF6B00",
          fontSize: "26px",
          animation: `${twinkle} 2.5s ease-in-out infinite 1s`,
          zIndex: 2,
        }}
      />
      <Avatar
        src={require("../../assets/images/login-handpik-logo_cropped.png")}
        sx={{
          width: { xs: 24, sm: 28 },
          height: { xs: 24, sm: 28 },
          bgcolor: "transparent",
          // The CSS filter below makes the image completely #FF8C38 orange
          img: { objectFit: "contain", filter: "brightness(0) saturate(100%) invert(60%) sepia(50%) saturate(1400%) hue-rotate(345deg) brightness(101%) contrast(100%)" },
        }}
      />
      <Typography
        sx={{
          fontFamily: "Poppins-SemiBold",
          fontWeight: 600,
          fontSize: { xs: "12px", sm: "14px" },
          letterSpacing: "0.5px",
        }}
      >
        Ask HANDPIK
      </Typography>
    </Box>
  );
};

export default GlobalAssistantButton;
